const STORAGE_KEY = "atw.crm.leads.listNav";

export type CrmLeadListNavEntry = {
  leadId: string;
  contact?: string;
};

export type CrmLeadListNavState = {
  entries: CrmLeadListNavEntry[];
  /** Query string (without leading ?) to restore list filters, e.g. view=company&q=foo */
  returnQuery: string;
};

export function buildCrmLeadsListReturnQuery(params: URLSearchParams): string {
  const next = new URLSearchParams();
  const view = params.get("view");
  if (view === "all" || view === "company" || view === "contacts") {
    next.set("view", view);
  }
  const q = (params.get("q") || "").trim();
  if (q) next.set("q", q);
  return next.toString();
}

export function writeCrmLeadListNav(state: CrmLeadListNavState): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* ignore quota / private mode */
  }
}

export function readCrmLeadListNav(): CrmLeadListNavState | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CrmLeadListNavState;
    if (!parsed || !Array.isArray(parsed.entries)) return null;
    return parsed;
  } catch {
    return null;
  }
}

function entryMatches(
  entry: CrmLeadListNavEntry,
  leadId: string,
  contact: string | null | undefined,
): boolean {
  if (entry.leadId !== leadId) return false;
  const entryContact = (entry.contact || "").trim();
  const activeContact = (contact || "").trim();
  if (!entryContact && !activeContact) return true;
  return entryContact.toLowerCase() === activeContact.toLowerCase();
}

export function resolveCrmLeadListNeighbors(
  leadId: string,
  contact?: string | null,
): {
  hasList: boolean;
  prev: CrmLeadListNavEntry | null;
  next: CrmLeadListNavEntry | null;
  returnQuery: string;
} {
  const state = readCrmLeadListNav();
  if (!state?.entries.length) {
    return { hasList: false, prev: null, next: null, returnQuery: "" };
  }

  let index = state.entries.findIndex((entry) => entryMatches(entry, leadId, contact));
  if (index < 0) {
    index = state.entries.findIndex((entry) => entry.leadId === leadId);
  }
  if (index < 0) {
    return { hasList: false, prev: null, next: null, returnQuery: state.returnQuery };
  }

  return {
    hasList: true,
    prev: index > 0 ? state.entries[index - 1]! : null,
    next: index < state.entries.length - 1 ? state.entries[index + 1]! : null,
    returnQuery: state.returnQuery,
  };
}

export function crmLeadDetailHref(entry: CrmLeadListNavEntry): string {
  const base = `/crm/leads/${encodeURIComponent(entry.leadId)}`;
  const contact = (entry.contact || "").trim();
  if (!contact) return base;
  return `${base}?contact=${encodeURIComponent(contact)}`;
}

export function crmLeadsListBackHref(returnQuery: string): string {
  return returnQuery ? `/crm/leads?${returnQuery}` : "/crm/leads";
}
